import MenuIcon from '@mui/icons-material/Menu';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import type { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { ReactNode, Ref } from 'react';

import { AccountMenu } from './AccountMenu';
import { GlobalSearch } from './GlobalSearch';
import { NotificationsPanel } from './NotificationsPanel';
import { OrganizationSwitcher } from './OrganizationSwitcher';

interface TopBarProps {
  /** Opens the navigation drawer on narrow screens. */
  onOpenNav: () => void;
  /** Folds or unfolds the left navigation on a desktop. */
  onToggleFold?: () => void;
  /** Whether the left navigation is folded to icons. */
  navFolded?: boolean;
  /** A row above the bar - the staff viewing banner, while it is on. */
  banner?: ReactNode;
  /** The whole bar, banner included, so the layout can measure its height. */
  ref?: Ref<HTMLElement>;
}

export function TopBar({
  onOpenNav,
  onToggleFold,
  navFolded = false,
  banner,
  ref,
}: TopBarProps) {
  /*
   * One control, two jobs, as the wireframe draws it beside the logo. On a
   * narrow screen the navigation is a drawer, and this opens it. On a desktop
   * the navigation is always there, and this folds it to icons and back.
   */
  const desktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

  return (
    <AppBar
      ref={ref}
      position="fixed"
      color="inherit"
      elevation={0}
      data-testid="top-bar"
      sx={{
        zIndex: (t) => t.zIndex.drawer + 1,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      {/* Above the bar, not below it: everything under the banner is then
          the organization's portal exactly as its own people see it. */}
      {banner}
      {/*
       * Three groups, so the organization can sit in the middle. The outer two
       * share the spare room equally, which centres it exactly while there is
       * room. When the controls need more than half, flexbox gives it to them
       * and the organization moves aside rather than sliding under search.
       */}
      {/* Closer together on a phone, so the avatar menu - the way to Log Out -
          stays on screen from 320px (arc-and-discovery-hardening, B29). */}
      <Toolbar sx={{ gap: { xs: 1, sm: 2 } }}>
        <Box
          sx={{
            flex: '1 1 0',
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
          }}
        >
          {desktop ? (
            <IconButton
              onClick={onToggleFold}
              aria-label={navFolded ? 'Expand navigation' : 'Collapse navigation'}
              // Says whether the full navigation is showing, so a screen reader
              // hears the state as well as the action.
              aria-expanded={!navFolded}
              edge="start"
              data-testid="top-bar-fold-nav-button"
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <IconButton
              onClick={onOpenNav}
              aria-label="Open navigation"
              edge="start"
              data-testid="top-bar-open-nav-button"
            >
              <MenuIcon />
            </IconButton>
          )}

          <Typography
            variant="h6"
            component="span"
            sx={{ fontWeight: 600 }}
            noWrap
            data-testid="top-bar-product-name"
          >
            ActivistARC
          </Typography>
        </Box>

        {/* The organization's logo or name: a plain mark for one
            organization, a menu for several. It reads the signed-in person
            itself rather than taking a name as a prop, because switching
            changes their role too. */}
        <OrganizationSwitcher />

        <Box
          sx={{
            flex: '1 1 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: { xs: 0.5, sm: 2 },
          }}
        >
          <GlobalSearch />

          <NotificationsPanel />

          <AccountMenu />
        </Box>
      </Toolbar>
    </AppBar>
  );
}
